"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calculator, RefreshCw, Droplets, CircleCheck as CheckCircle2, TrendingDown, ShieldCheck, Activity, Layers, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const workflowSteps = [{
  step: "01",
  title: "HPLC Effluent Inflow",
  desc: "Real-time solvent detection stream",
  icon: Droplets,
  color: "border-chart-3 text-chart-3"
}, {
  step: "02",
  title: "Optical Purity Separation",
  desc: "High-precision baseline discrimination",
  icon: Activity,
  color: "border-primary text-primary"
}, {
  step: "03",
  title: "Closed-Loop Recovery",
  desc: "Up to 90% unadulterated mobile phase return",
  icon: RefreshCw,
  color: "border-sidebar-foreground text-sidebar-foreground"
}];
const impactStats = [{
  label: "Solvent Reduction",
  value: "Up to 90%",
  icon: TrendingDown
}, {
  label: "Disposal Compliance Focus",
  value: "Structured Compliance Support",
  icon: ShieldCheck
}, {
  label: "Payback Period",
  value: "< 6 Months",
  icon: CheckCircle2
}];
export default function SolventHero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="herosolventwaste" data-nav="dark" className="relative overflow-hidden bg-sidebar text-sidebar-foreground py-16 md:py-24 lg:py-32">
      {/* Background Decorative Grid & Glow Elements */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-chart-3/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--sidebar-border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--sidebar-border))_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20" />
      </div>

      <div className="page-container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-2 text-xs text-sidebar-foreground/70 sm:mb-8">
          <Link to="/HomePage" className="transition-colors hover:text-sidebar-foreground">
            Home
          </Link>
          <ChevronRight className="size-3.5 text-sidebar-foreground/40" />
          <Link to="/SolutionsPage" className="transition-colors hover:text-sidebar-foreground">
            Solutions
          </Link>
          <ChevronRight className="size-3.5 text-sidebar-foreground/40" />
          <span className="font-medium text-sidebar-foreground" aria-current="page">
            HPLC Solvent Recovery (ReSolve)
          </span>
        </nav>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column (8 cols): Primary Copy & CTAs */}
          <motion.div initial="hidden" whileInView="show" viewport={{
          once: true
        }} variants={fadeIn} className="flex flex-col space-y-6 lg:col-span-8 lg:pr-8">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="outline" className="border-primary/40 bg-sidebar-accent/60 px-3 py-1 text-xs tracking-wide text-sidebar-foreground backdrop-blur-sm">
                <Layers className="size-3.5 text-primary" />
                <span>Sustainable Lab Economics</span>
              </Badge>
              <Badge variant="outline" className="border-sidebar-border bg-sidebar/50 px-3 py-1 text-xs text-sidebar-foreground/80">
                HPLC & UHPLC Recycling Systems
              </Badge>
            </div>

            <h1 className="text-3xl font-bold tracking-tight text-sidebar-foreground sm:text-4xl md:text-5xl lg:text-5xl lg:leading-[1.15]">
              Eliminate HPLC Solvent Waste and Reclaim Laboratory Operating Margins
            </h1>

            <p className="max-w-2xl text-base text-sidebar-foreground/80 sm:text-lg sm:leading-relaxed">
              High solvent consumption, disposal costs, and sustainability pressure impact lab economics.
              Automate solvent recovery directly in your chromatography workflows with zero method interference.
            </p>

            {/* CTAs */}
            <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground shadow-md hover:bg-primary/90 focus-visible:ring-primary">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Discuss Your Application</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-sidebar-accent/40 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-sidebar-ring">
                <Link to="/CalculatePotentialSavingsPage" className="inline-flex items-center gap-2">
                  <Calculator className="size-4 text-primary" />
                  <span>Calculate Potential Savings</span>
                </Link>
              </Button>
            </div>

            {/* Impact Highlights Grid */}
            <div className="grid grid-cols-1 gap-4 pt-6 sm:grid-cols-3 border-t border-sidebar-border/60">
              {impactStats.map((stat, i) => {
              const IconComponent = stat.icon;
              return <div key={stat.label} data-index={i} className="flex items-center gap-3 rounded-lg border border-sidebar-border/40 bg-sidebar-accent/20 p-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-primary">
                      <IconComponent className="size-4" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-sidebar-foreground">
                        {stat.value}
                      </div>
                      <div className="text-xs text-sidebar-foreground/70">
                        {stat.label}
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Right Column (4 cols): Closed-Loop Graphic & Flow */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.15
        }} className="lg:col-span-4">
            <div className="relative rounded-2xl border border-sidebar-border bg-sidebar-accent/30 p-6 backdrop-blur-md shadow-xl">
              {/* Graphic Header */}
              <div className="mb-6 flex items-center justify-between border-b border-sidebar-border/80 pb-4">
                <div className="flex items-center gap-2">
                  <div className="flex size-7 items-center justify-center rounded-md bg-primary/20 text-primary">
                    <RefreshCw className="size-4" />
                  </div>
                  <span className="text-sm font-semibold tracking-wide text-sidebar-foreground">
                    Closed-Loop Recovery
                  </span>
                </div>
                <Badge variant="outline" className="border-chart-3/40 bg-chart-3/10 text-[11px] text-chart-3">
                  Continuous Flow
                </Badge>
              </div>

              {/* Workflow Interactive Loop Scheme */}
              <div className="relative space-y-4">
                {workflowSteps.map((step, i) => {
                const StepIcon = step.icon;
                return <div key={step.step} data-index={i} className="group relative flex items-start gap-4 rounded-xl border border-sidebar-border/70 bg-sidebar/70 p-3.5 transition-colors duration-200 hover:border-primary/50 hover:bg-sidebar-accent/50">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-sidebar-border bg-sidebar-accent text-sidebar-foreground">
                        <StepIcon className="size-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h2 className="text-sm font-semibold text-sidebar-foreground">
                            {step.title}
                          </h2>
                          <span className="text-[11px] font-mono text-sidebar-foreground/50">
                            {step.step}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-sidebar-foreground/75">
                          {step.desc}
                        </p>
                      </div>
                    </div>;
              })}

                {/* Closed Loop Visual Connector Bar */}
                <div className="mt-5 rounded-xl border border-primary/30 bg-primary/10 p-3 text-center">
                  <div className="flex items-center justify-center gap-2 text-xs font-medium text-primary">
                    <CheckCircle2 className="size-4" />
                    <span>Pure Mobile Phase Re-enters Solvent Reservoir</span>
                  </div>
                </div>
              </div>

              {/* Bottom Quick Note */}
              <div className="mt-5 flex items-center justify-between text-xs text-sidebar-foreground/60">
                <span>Compatibility: Isocratic & Gradient</span>
                <span className="font-mono text-[11px] text-primary">Regulated Lab Compatible</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}